import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit3, Trash2 } from 'lucide-react'
import { commandCenterApi } from '../services/api/commandCenter'
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dialog,
  Field,
  FileUploadField,
  IconButton,
  Input,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchField,
  StatePanel,
} from './common'
import styles from './CreatorManager.module.css'

const EMPTY_ARRAY = []
const CREATOR_SEARCH_DEBOUNCE_MS = 250

function CommandCenterCreators() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [apiSearch, setApiSearch] = useState('')
  const [editingCreator, setEditingCreator] = useState(null)
  const [displayName, setDisplayName] = useState('')
  const [selectedOrganizations, setSelectedOrganizations] = useState(new Set())
  const [avatarFile, setAvatarFile] = useState(null)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [archiveOffer, setArchiveOffer] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => setApiSearch(search.trim()), CREATOR_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const creatorsQuery = useQuery({
    queryKey: ['command-center-creators', apiSearch],
    queryFn: () => commandCenterApi.listCreators(apiSearch ? { q: apiSearch } : {}),
    placeholderData: (previousData) => previousData,
  })
  const organizationsQuery = useQuery({
    queryKey: ['command-center-organizations'],
    queryFn: () => commandCenterApi.listOrganizations(),
  })
  const creators = creatorsQuery.data?.creators || EMPTY_ARRAY
  const organizations = organizationsQuery.data?.organizations || EMPTY_ARRAY

  const closeDialog = () => {
    setEditingCreator(null)
    setDisplayName('')
    setSelectedOrganizations(new Set())
    setAvatarFile(null)
  }

  const openCreate = () => {
    closeDialog()
    setEditingCreator({ id: null, memberships: [] })
  }

  const openEdit = (creator) => {
    setEditingCreator(creator)
    setDisplayName(creator.display_name)
    setSelectedOrganizations(new Set((creator.memberships || []).map((membership) => membership.organization_id)))
    setAvatarFile(null)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const existingMemberships = new Map(
        (editingCreator?.memberships || []).map((membership) => [membership.organization_id, membership])
      )
      const memberships = [...selectedOrganizations].map((organizationId) => ({
        organization_id: organizationId,
        allowed_offerings: existingMemberships.get(organizationId)?.allowed_offerings || [],
      }))
      if (editingCreator?.id) {
        await commandCenterApi.updateCreator(editingCreator.id, {
          display_name: displayName.trim(),
          memberships,
        })
        if (avatarFile) await commandCenterApi.uploadCreatorAvatar(editingCreator.id, avatarFile)
        return 'Creator updated'
      }
      const result = await commandCenterApi.createCreator({
        display_name: displayName.trim(),
        memberships,
      })
      if (avatarFile) await commandCenterApi.uploadCreatorAvatar(result.creator.id, avatarFile)
      return 'Creator created'
    },
    onSuccess: (successMessage) => {
      queryClient.invalidateQueries({ queryKey: ['command-center-creators'] })
      queryClient.invalidateQueries({ queryKey: ['cc-creators'] })
      setMessage({ type: 'success', text: successMessage })
      closeDialog()
    },
    onError: (error) => {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to save creator',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (creator) => commandCenterApi.deleteCreator(creator.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['command-center-creators'] })
      queryClient.invalidateQueries({ queryKey: ['cc-creators'] })
      setMessage({ type: 'success', text: 'Creator deleted' })
      setConfirmDelete(null)
    },
    onError: (error, creator) => {
      setConfirmDelete(null)
      if (error.response?.status === 409 && error.response?.data?.code === 'creator_has_content') {
        setArchiveOffer(creator)
        return
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to delete creator',
      })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (creator) => commandCenterApi.archiveCreator(creator.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['command-center-creators'] })
      queryClient.invalidateQueries({ queryKey: ['cc-creators'] })
      setMessage({ type: 'success', text: 'Creator archived' })
      setArchiveOffer(null)
    },
    onError: (error) => {
      setArchiveOffer(null)
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to archive creator',
      })
    },
  })

  const columns = useMemo(() => [
    {
      key: 'display_name',
      label: 'Creator',
      sortable: true,
      render: (creator) => (
        <div className={styles.creatorCell}>
          {creator.avatar_url ? (
            <img src={creator.avatar_url} alt="" className={styles.avatar} />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {(creator.display_name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <span>{creator.display_name}</span>
        </div>
      ),
    },
    {
      key: 'organizations',
      label: 'Organizations',
      render: (creator) => (
        <span>{(creator.memberships || []).map((membership) => membership.organization_name).join(', ') || 'None'}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (creator) => (
        <div className={styles.actionButtons}>
          <IconButton label="Edit creator" variant="ghost" onClick={() => openEdit(creator)}>
            <Edit3 size={16} />
          </IconButton>
          <IconButton
            label="Delete creator"
            variant="danger"
            className={styles.removeButton}
            onClick={() => setConfirmDelete(creator)}
          >
            <Trash2 size={16} />
          </IconButton>
        </div>
      ),
    },
  ], [])

  const toggleOrganization = (organizationId) => {
    setSelectedOrganizations((current) => {
      const next = new Set(current)
      if (next.has(organizationId)) next.delete(organizationId)
      else next.add(organizationId)
      return next
    })
  }

  const loading = creatorsQuery.isLoading || organizationsQuery.isLoading
  const failed = creatorsQuery.isError || organizationsQuery.isError

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Creators"
        context="Command Center"
        primaryAction={<Button onClick={openCreate}>Add creator</Button>}
      />

      {message.text ? (
        <Alert variant={message.type === 'error' ? 'error' : 'success'}>{message.text}</Alert>
      ) : null}

      {loading ? (
        <StatePanel state="loading" title="Loading creators…" />
      ) : failed ? (
        <StatePanel
          state="error"
          title="Creators could not load"
          description="No creator records were changed."
          onRetry={() => {
            creatorsQuery.refetch()
            organizationsQuery.refetch()
          }}
        />
      ) : creators.length === 0 && !search.trim() ? (
        <StatePanel
          state="empty"
          title="No creators yet"
          description="Create a creator and assign it to one or more organizations."
        />
      ) : (
        <>
          <PageToolbar
            label="Creator controls"
            search={(
              <SearchField
                label="Search creators"
                placeholder="Search creators…"
                value={search}
                onChange={setSearch}
                loading={creatorsQuery.isFetching}
              />
            )}
          />
          {creators.length === 0 ? (
            <StatePanel
              state="search-empty"
              title="No creators match"
              description={`No creators match “${search.trim()}”.`}
              onClear={() => setSearch('')}
            />
          ) : (
            <DataTable
              label="Creators"
              data={creators}
              columns={columns}
              density="compact"
              paginate
              pageSize={20}
            />
          )}
        </>
      )}

      <Dialog
        open={!!editingCreator}
        title={editingCreator?.id ? 'Edit creator' : 'Add creator'}
        description="Assign this creator to every organization that should be able to publish as them."
        onClose={closeDialog}
        footer={(
          <>
            <Button variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!displayName.trim() || selectedOrganizations.size === 0 || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save creator'}
            </Button>
          </>
        )}
      >
        <div className={styles.modalStack}>
          <FileUploadField
            label="Avatar"
            variant="avatar"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(event) => setAvatarFile(event.target.files?.[0] || null)}
            triggerLabel="Choose image"
            filename={avatarFile?.name}
            previewSrc={editingCreator?.avatar_url || null}
            placeholderText={(displayName || '?').charAt(0).toUpperCase()}
            onClear={avatarFile ? () => setAvatarFile(null) : undefined}
            helper="The feed uses a square rendition and keeps the original image."
          />
          <Field label="Display name" required>
            <Input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Enter display name"
              data-autofocus
            />
          </Field>
          <Field label="Organizations" required>
            <div className={styles.organizationChecklist}>
              {organizations.map((organization) => (
                <Checkbox
                  key={organization.id}
                  checked={selectedOrganizations.has(organization.id)}
                  onChange={() => toggleOrganization(organization.id)}
                  label={organization.name}
                />
              ))}
            </div>
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete creator"
        message={`Permanently delete “${confirmDelete?.display_name || ''}”? This removes the creator from every organization and cannot be undone.`}
        variant="danger"
        confirmLabel="Delete creator"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />

      <ConfirmDialog
        open={!!archiveOffer}
        title="Creator has published content"
        message={`“${archiveOffer?.display_name || ''}” cannot be deleted because posts reference this creator. Archive the creator to remove them from every roster and picker; published posts keep showing their author.`}
        confirmLabel="Archive creator"
        loading={archiveMutation.isPending}
        onConfirm={() => archiveMutation.mutate(archiveOffer)}
        onCancel={() => setArchiveOffer(null)}
      />
    </PageLayout>
  )
}

export default CommandCenterCreators
